import { useEffect, useState } from 'react';
import { BellRing, MonitorCheck, Send } from 'lucide-react';
import { Card } from './UI';
import {
  getNotificationPreferences,
  setNotificationPreferences,
  getSystemNotificationPermission,
  requestSystemNotificationPermission,
  sendTestSystemNotification,
  notificationRuntime,
} from '../lib/system-notifications';

export default function NotificationSettingsCard(){
  const[prefs,setPrefs]=useState(()=>getNotificationPreferences());
  const[permission,setPermission]=useState('prompt');
  const[busy,setBusy]=useState(false);
  const[notice,setNotice]=useState('');
  const runtime=notificationRuntime();

  const refresh=()=>getSystemNotificationPermission().then(setPermission);
  useEffect(()=>{refresh();},[]);

  const update=patch=>{
    const next=setNotificationPreferences(patch);
    setPrefs(next);
  };

  const enable=async()=>{
    setBusy(true);
    setNotice('');
    try{
      const result=await requestSystemNotificationPermission();
      setPermission(result);
      if(result==='granted'){
        update({enabled:true});
        const sent=await sendTestSystemNotification();
        setNotice(sent?'Computer notifications are enabled. A test notification has been sent.':'Permission was granted, but the test notification could not be delivered.');
      }else{
        update({enabled:false});
        if(result==='denied')setNotice('Notifications are blocked for this CRM. Allow notifications for crm.sousamurraygroup.co.uk in Microsoft Edge site permissions, then try again.');
        else if(result==='blocked')setNotice('The Windows/Edge permission prompt did not complete. Open Microsoft Edge site permissions for crm.sousamurraygroup.co.uk, set Notifications to Allow, then return here and try again.');
        else if(result==='prompt')setNotice('Notification permission was not granted. Please choose Allow when Microsoft Edge asks.');
        else setNotice('System notifications are not available in this environment.');
      }
    }catch(error){
      update({enabled:false});
      setNotice(error?.message||'Customer Operations could not enable computer notifications.');
    }finally{setBusy(false)}
  };

  const test=async()=>{
    setBusy(true);
    try{await sendTestSystemNotification();}finally{setBusy(false)}
  };

  return <div id="notifications">
    <Card title="Computer notifications">
      <div className="notification-settings-intro">
        <span className="notification-settings-icon"><BellRing size={21}/></span>
        <div><strong>Customer Operations notifications</strong><p className="govuk-body">Receive proper computer notifications for new work while Customer Operations is running. Notification text is deliberately privacy-minimised and does not show customer names, email content or other sensitive case details.</p></div>
      </div>
      <div className="notification-settings-status">
        <MonitorCheck size={18}/>
        <span><strong>{prefs.enabled&&permission==='granted'?'Enabled':'Not enabled'}</strong><small>{runtime==='tauri'?'Native Tauri / Windows notification service':runtime==='browser'?'Secure browser / Windows notification service':'This environment does not expose system notifications'} · permission: {permission}</small></span>
        {runtime!=='unsupported'&&(prefs.enabled&&permission==='granted'
          ?<button className="button secondary small govuk-button govuk-button--secondary" onClick={()=>update({enabled:false})}>Turn off</button>
          :<button className="button primary small govuk-button" onClick={enable} disabled={busy}>{busy?'Enabling…':'Enable computer notifications'}</button>)}
      </div>
      {notice&&<div className={`operation-notice ${permission==='granted'?'':'error'}`}>{notice}</div>}
      <div className="notification-settings-grid">
        <label className="govuk-label"><input type="checkbox" checked={prefs.email} onChange={e=>update({email:e.target.checked})}/><span><strong>Inbound email</strong><small>Notify when a new unread inbound message reaches Customer Operations.</small></span></label>
        <label className="govuk-label"><input type="checkbox" checked={prefs.cases} onChange={e=>update({cases:e.target.checked})}/><span><strong>Assigned cases & complaints</strong><small>Notify when a request or complaint is assigned to you.</small></span></label>
        <label className="govuk-label"><input type="checkbox" checked={prefs.tasks} onChange={e=>update({tasks:e.target.checked})}/><span><strong>Assigned tasks</strong><small>Notify when a live Customer Operations task is assigned to you.</small></span></label>
      </div>
      <div className="settings-footer notification-settings-footer">
        <span>Windows notification permission remains under your device/browser control.</span>
        <button className="button secondary small govuk-button govuk-button--secondary" onClick={test} disabled={busy||permission!=='granted'}><Send size={14}/>Send test notification</button>
      </div>
    </Card>
  </div>;
}
